import { getAccessToken, saveAccessToken } from "./storage.js";

const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export {
  isAudioPlaybackStoppedError,
  playAudioBlob,
  primeAudioPlayback,
  stopAudioPlayback,
} from "./audio-playback.js";

/** 统一表示无状态网关返回的可展示错误。 */
export class GatewayError extends Error {
  /**
   * 创建网关错误。
   *
   * @param {string} message 用户可理解的错误信息。
   * @param {number} status HTTP 状态码。
   * @param {string} code 稳定错误码。
   */
  constructor(message, status = 0, code = "gateway_error") {
    super(message);
    this.name = "GatewayError";
    this.status = status;
    this.code = code;
  }
}

/**
 * 将 API 错误响应解析为统一错误对象。
 *
 * @param {Response} response HTTP 响应。
 * @returns {Promise<GatewayError>} 解析后的错误。
 */
async function responseError(response) {
  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }
  return new GatewayError(payload.message || `服务返回 HTTP ${response.status}`, response.status, payload.code || "gateway_error");
}

/**
 * 封装 PWA 到同源 Pages Functions 的鉴权和数据请求。
 */
export class GatewayClient {
  /**
   * 创建网关客户端。
   *
   * @param {string} basePath 同源 API 根路径。
   * @param {{requestTimeoutMs?: number}} options 单次网络请求超时设置。
   */
  constructor(basePath = "/api", { requestTimeoutMs = DEFAULT_REQUEST_TIMEOUT_MS } = {}) {
    this.basePath = basePath.replace(/\/$/, "");
    this.requestTimeoutMs = Math.max(1, Number(requestTimeoutMs) || DEFAULT_REQUEST_TIMEOUT_MS);
  }

  /**
   * 发起带统一超时和中文网络错误的请求。
   *
   * @param {string} url 请求地址。
   * @param {RequestInit} options fetch 配置。
   * @returns {Promise<Response>} 服务端响应。
   */
  async performRequest(url, options = {}) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), this.requestTimeoutMs);
    try {
      return await fetch(url, { ...options, signal: controller.signal });
    } catch (error) {
      if (error?.name === "AbortError") throw new GatewayError("请求超时，请检查网络后重试", 408, "request_timeout");
      if (error instanceof TypeError) throw new GatewayError("网络连接失败，请检查网络后重试", 0, "network_error");
      throw error;
    } finally {
      window.clearTimeout(timer);
    }
  }

  /**
   * 处理失败响应，并在鉴权失效时删除本机令牌。
   *
   * @param {Response} response 服务端响应。
   * @param {boolean} clearAuthOnUnauthorized 是否在 401 时删除本机令牌。
   * @returns {Promise<void>} 响应通过校验后的结果。
   */
  async requireSuccessfulResponse(response, clearAuthOnUnauthorized = false) {
    if (response.ok) return;
    if (clearAuthOnUnauthorized && response.status === 401) await saveAccessToken("");
    throw await responseError(response);
  }

  /**
   * 使用单用户激活码换取长期访问令牌。
   *
   * @param {string} activationCode 用户输入的激活码。
   * @returns {Promise<object>} 网关能力信息。
   */
  async activate(activationCode) {
    const response = await this.performRequest(`${this.basePath}/activate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ activationCode: String(activationCode || "").trim() }),
    });
    await this.requireSuccessfulResponse(response);
    const payload = await response.json();
    await saveAccessToken(payload.token);
    return payload;
  }

  /**
   * 清除本机访问令牌。
   *
   * @returns {Promise<void>} 退出完成结果。
   */
  async logout() {
    await saveAccessToken("");
  }

  /**
   * 检查当前令牌并读取已配置的模型能力。
   *
   * @returns {Promise<object>} 服务商能力和公开模型名称。
   */
  async bootstrap() {
    return this.requestJson("/bootstrap", { method: "GET" });
  }

  /**
   * 执行一个结构化文本或音频 AI 操作。
   *
   * @param {string} action 固定操作名，例如 daily、transcribe 或 assess。
   * @param {object} payload 该操作的结构化上下文。
   * @param {Blob|null} audio 可选 WAV 录音。
   * @returns {Promise<object>} 模型结构化结果。
   */
  async runAI(action, payload = {}, audio = null) {
    const token = await getAccessToken();
    if (!token) throw new GatewayError("请先使用激活码连接服务", 401, "activation_required");
    const form = new FormData();
    form.append("request", JSON.stringify({ action, ...payload }));
    if (audio) form.append("audio", audio, "recording.wav");
    const response = await this.performRequest(`${this.basePath}/ai`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    await this.requireSuccessfulResponse(response, true);
    return response.json();
  }

  /**
   * 请求模型合成语音并返回浏览器可播放 Blob。
   *
   * @param {string} text 需要朗读的英文文本。
   * @param {string} provider gemini、openai 或 elevenlabs。
   * @param {string} style neutral 或 american。
   * @returns {Promise<Blob>} 合成音频。
   */
  async synthesize(text, provider, style = "neutral") {
    const token = await getAccessToken();
    if (!token) throw new GatewayError("请先使用激活码连接服务", 401, "activation_required");
    const response = await this.performRequest(`${this.basePath}/tts`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ text, provider, style }),
    });
    await this.requireSuccessfulResponse(response, true);
    return response.blob();
  }

  /**
   * 发送带访问令牌的 JSON 请求。
   *
   * @param {string} path API 相对路径。
   * @param {RequestInit} options fetch 配置。
   * @returns {Promise<object>} JSON 响应。
   */
  async requestJson(path, options = {}) {
    const token = await getAccessToken();
    if (!token) throw new GatewayError("请先使用激活码连接服务", 401, "activation_required");
    const headers = new Headers(options.headers || {});
    headers.set("Authorization", `Bearer ${token}`);
    const response = await this.performRequest(`${this.basePath}${path}`, { ...options, headers });
    await this.requireSuccessfulResponse(response, true);
    return response.json();
  }
}
